/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import sendEmail from '@/modules/emails/send-email';
import compileChangeObject from '@/utilities/publishing/compile-change-object';

export const COLLECTION_SOLUTION = {
  SHOPIFY_APP: 'Shopify App',
  COLLECTIONS_WIDGET: 'Collections Widget',
  COLLECTIONS_API: 'Collections API',
};

const sendSignupEmails = ({
  tokenType, accessToken, getCarrierName,
  emailsToSend, data, adminEmail,
  adminNotificationEmail, collectionWidgetEmail,
  consumerUrl,
}) => {
  if (!emailsToSend?.length > 0) return null;

  const merchantInfo = compileChangeObject(data);
  const merchant = merchantInfo.applicant;

  const emailBody = (email) => {
    switch (email.templateID) {
      case 'merchant_request_notification': {
        const { collectionSolutionRequired, ...merchantRest } = merchant;
        return {
          ...merchantRest,
          reviewRequestCta: `${window.location.origin}/${merchant.companyName}/login`,
          contactEmail: merchant.emailAddress,
          contactPhone: merchant.phoneNumber,
          eParcel: !!merchant.eParcel,
          myPostBusiness: !!merchant.myPostBusiness,
          startrackPremium: !!merchant.startrackPremium,
          shopifyAppSolution: collectionSolutionRequired === COLLECTION_SOLUTION.SHOPIFY_APP,
          collectionsWidgetSolution: collectionSolutionRequired === COLLECTION_SOLUTION.COLLECTIONS_WIDGET,
          collectionsAPISolution: collectionSolutionRequired === COLLECTION_SOLUTION.COLLECTIONS_API,
          platformPartner: merchant.platformPartner,
        };
      }
      case 'location_finder_info':
        return {
          contactEmail: merchant.emailAddress,
          companyName: merchant.companyName,
          contactName: merchant.contactName,
          contactPhone: merchant.phoneNumber,
          merchantChargeAccount: merchant.merchantChargeAccount,
          customersToUse: merchant.customersToUse,
          collectionWidget: merchant.collectionWidget,
          eParcel: !!merchant.eParcel,
          myPostBusiness: !!merchant.myPostBusiness,
          startrackPremium: !!merchant.startrackPremium,
          shopifyAppSolution: merchant.collectionSolutionRequired === COLLECTION_SOLUTION.SHOPIFY_APP,
          collectionsWidgetSolution: merchant.collectionSolutionRequired === COLLECTION_SOLUTION.COLLECTIONS_WIDGET,
          collectionsAPISolution: merchant.collectionSolutionRequired === COLLECTION_SOLUTION.COLLECTIONS_API,
        };
      case 'signup_from_shopify_request_notification':
        return {
          href: `${window.location.origin}/merchants/requests`,
          merchantDetails: {
            email: merchant.emailAddress,
            companyName: `${email?.customConsumerPortalUrl || consumerUrl}/${merchant.companyName}`,
            phoneNumber: merchant.phoneNumber,
            name: `${merchant.firstName} ${merchant.lastName}`,
            legalEntity: merchant.legalEntity,
            returnRange: merchant.incomeRange,
            turnoverRange: merchant.turnoverRange,
            industry: merchant.industry,
          },
        };
      default:
        return {};
    }
  };

  const emailBcc = [
    {
      name: merchant.contactName,
      email: '%EMAIL%',
    },
  ];

  const getRecipientEmail = (email) => {
    if (email.sendAdminOnly) {
      return adminNotificationEmail;
    }

    if (email.useWidgetRecipientEmail) {
      return collectionWidgetEmail;
    }

    return '%EMAIL%';
  };

  return emailsToSend.forEach((email) => {
    if (email) {
      if (email.sendIfSelected) {
        const fieldsToCheck = Array.isArray(email.sendIfSelected) ? email.sendIfSelected : [email.sendIfSelected];
        if (!fieldsToCheck.some((field) => merchant[field])) {
          return null;
        }
      }
      sendEmail({
        templateId: email.templateID,
        authTokenString: `${tokenType} ${accessToken}`,
        orgId: getCarrierName(),
        setStatus: () => {},
        getConfigValue: () => {},
        requestData: {
          ...(email.bccEnabled && { bcc: emailBcc }),
          recipient: {
            name: email.title,
            email: getRecipientEmail(email),
          },
          ...(Object.entries(emailBody(email)).length > 0 && { substitutionData: emailBody(email) }),
        },
        emailAddress: email.sendAdminOnly ? adminEmail : merchant.emailAddress,
      });
    }
    return null;
  });
};

export default sendSignupEmails;
